import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { filteredAgenciesQuery } from '../../api/queries';
import { Category, ItemConnectionSort } from '../../graphql/graphql';
import {
  AGENCY_PAGE_DESKTOP_CARDS_LIMIT,
  AgencyFilterDealTypeEnum,
  COMMERCIAL_CATEGORY_SLUGS,
  DEFAULT_DESKTOP_CARDS_LIMIT
} from '../constants/agencyPage';
import { getI18nForLang } from './getI18nForLang';

export const DEAL_TYPE_TO_SLUG: Record<AgencyFilterDealTypeEnum, string> = {
  [AgencyFilterDealTypeEnum.lease]: 'kiraye',
  [AgencyFilterDealTypeEnum.sale]: 'alqi-satqi'
};

export const SLUG_TO_DEAL_TYPE: Record<string, AgencyFilterDealTypeEnum> = {
  ...Object.fromEntries(
    Object.entries(DEAL_TYPE_TO_SLUG).map(([k, v]) => [
      v,
      k as AgencyFilterDealTypeEnum
    ])
  ),
  icare: AgencyFilterDealTypeEnum.lease
};

const SSR_CARDS_LIMIT = AGENCY_PAGE_DESKTOP_CARDS_LIMIT * 2;

type CategoryIndex = {
  bySlug: Map<string, string>;
  byId: Map<string, string>;
};

type CategoryLike = Pick<Category, 'id' | 'slug'>;

const buildCategoryIndex = (categories: CategoryLike[]): CategoryIndex =>
  categories.reduce<CategoryIndex>(
    (acc, { slug, id }) => {
      const lastSlug = slug?.split('/').pop();
      if (lastSlug) {
        acc.bySlug.set(lastSlug, id);
        acc.byId.set(id, lastSlug);
      }
      return acc;
    },
    { bySlug: new Map(), byId: new Map() }
  );

export const parseAgencySegments = (
  segments: string[] = [],
  categories: CategoryLike[] = []
): { dealType: AgencyFilterDealTypeEnum | null; category: string | null } => {
  const { bySlug } = buildCategoryIndex(categories);

  return segments.reduce(
    (acc, segment) => {
      if (Object.hasOwn(SLUG_TO_DEAL_TYPE, segment)) {
        acc.dealType = SLUG_TO_DEAL_TYPE[segment];
      } else {
        const categoryId = bySlug.get(segment);
        if (categoryId) acc.category = categoryId;
      }
      return acc;
    },
    {
      dealType: null as AgencyFilterDealTypeEnum | null,
      category: null as string | null
    }
  );
};

type BuildAgencyUrlParams = {
  agencySlug: string;
  clickedId: string;
  activeLeasedCategory: AgencyFilterDealTypeEnum[];
  categories: CategoryLike[];
};

const getLeaseSlug = (categorySlug: string | null): string =>
  categorySlug && COMMERCIAL_CATEGORY_SLUGS.has(categorySlug)
    ? 'icare'
    : 'kiraye';

export const buildAgencyUrl = ({
  agencySlug,
  clickedId,
  activeLeasedCategory,
  categories
}: BuildAgencyUrlParams): string => {
  const basePath = `/agentlikler/${agencySlug}`;
  const { byId } = buildCategoryIndex(categories);

  const isDealType = clickedId in DEAL_TYPE_TO_SLUG;

  const dealType: AgencyFilterDealTypeEnum | null = (() => {
    if (isDealType) return clickedId as AgencyFilterDealTypeEnum;
    if (activeLeasedCategory.length === 1) return activeLeasedCategory[0];
    return null;
  })();

  const categorySlug = !isDealType ? (byId.get(clickedId) ?? null) : null;

  const dealSlug = (() => {
    if (dealType === AgencyFilterDealTypeEnum.lease)
      return getLeaseSlug(categorySlug);
    if (dealType) return DEAL_TYPE_TO_SLUG[dealType];
    return null;
  })();

  const parts = [dealSlug, categorySlug].filter(Boolean);

  return parts.length ? `${basePath}/${parts.join('/')}` : basePath;
};

export const getAgencyFilterCardsLimit = (
  isLarge: boolean,
  isSmall: boolean,
  isClient: boolean,
  desktopCardsPerLine = DEFAULT_DESKTOP_CARDS_LIMIT
): number => {
  if (isLarge || !isClient) return desktopCardsPerLine * 2;
  if (isSmall) return 4;
  return 6;
};

type AgencyItemsFilter = {
  agencyId: string;
  categoryId?: string | null;
  dealType?: AgencyFilterDealTypeEnum | null;
};

const buildItemsFilter = ({
  agencyId,
  categoryId,
  dealType
}: AgencyItemsFilter) => ({
  agencyId,
  ...(categoryId ? { categoryId } : {}),
  ...(dealType ? { leased: dealType === AgencyFilterDealTypeEnum.lease } : {})
});

const prefetchItems = (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  filter: AgencyItemsFilter,
  first: number
) =>
  apolloClient.query({
    query: filteredAgenciesQuery,
    variables: {
      filter: buildItemsFilter(filter),
      first,
      sort: ItemConnectionSort.BumpedAtDesc
    }
  });

type GetAgencyInitialItemsParams = {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  agencyId: string;
  dealType: AgencyFilterDealTypeEnum | null;
  category: string | null;
  itemCategories: { id: string }[];
};

export const getAgencyInitialItems = async ({
  apolloClient,
  agencyId,
  dealType,
  category,
  itemCategories
}: GetAgencyInitialItemsParams): Promise<{ totalCount: number }> => {
  if (dealType && category) {
    const result = await prefetchItems(
      apolloClient,
      { agencyId, categoryId: category, dealType },
      SSR_CARDS_LIMIT * 2
    );
    return {
      totalCount: result.data?.itemsConnection?.totalCount ?? 0
    };
  }

  if (category) {
    const [leaseResult, saleResult] = await Promise.all([
      prefetchItems(
        apolloClient,
        {
          agencyId,
          categoryId: category,
          dealType: AgencyFilterDealTypeEnum.lease
        },
        SSR_CARDS_LIMIT
      ),
      prefetchItems(
        apolloClient,
        {
          agencyId,
          categoryId: category,
          dealType: AgencyFilterDealTypeEnum.sale
        },
        SSR_CARDS_LIMIT
      )
    ]);
    return {
      totalCount:
        (leaseResult.data?.itemsConnection?.totalCount ?? 0) +
        (saleResult.data?.itemsConnection?.totalCount ?? 0)
    };
  }

  if (dealType) {
    const results = await Promise.all(
      itemCategories
        .slice(0, 2)
        .map(({ id }) =>
          prefetchItems(
            apolloClient,
            { agencyId, categoryId: id, dealType },
            SSR_CARDS_LIMIT
          )
        )
    );
    return {
      totalCount: results.reduce(
        (sum, r) => sum + (r.data?.itemsConnection?.totalCount ?? 0),
        0
      )
    };
  }

  return { totalCount: 0 };
};

type AgencySeoContent = {
  title: string | null;
  description: string | null;
  h1: string | null;
};

export const buildAgencySeoContent = (
  name: string | null | undefined,
  lng: string,
  segments: string[]
): AgencySeoContent => {
  const { t } = getI18nForLang(lng);
  const agencyVars = { agency_name: name };

  const dealSlug =
    segments.find(s => Object.hasOwn(SLUG_TO_DEAL_TYPE, s)) ?? null;
  const categorySlug =
    segments.find(s => !Object.hasOwn(SLUG_TO_DEAL_TYPE, s)) ?? null;
  const dealKey = dealSlug ? SLUG_TO_DEAL_TYPE[dealSlug] : null;

  if (!dealKey && !categorySlug) {
    return {
      title: name ? t('pages.agency.meta_title', agencyVars) : null,
      description: name ? t('pages.agency.meta_description', agencyVars) : null,
      h1: name ?? null
    };
  }

  const base = 'pages.agency.seo';
  let title;
  let description;
  let h1;

  if (dealKey && !categorySlug) {
    title = t(`${base}.title_template`, {
      ...agencyVars,
      phrase: t(`${base}.deal.${dealKey}.title_phrase`)
    });

    description = t(`${base}.deal.${dealKey}.description`, agencyVars);

    h1 = t(`${base}.h1_template`, {
      ...agencyVars,
      suffix: t(`${base}.deal.${dealKey}.h1`)
    });
  } else if (!dealKey && categorySlug) {
    title = t(`${base}.title_template`, {
      ...agencyVars,
      phrase: t(`${base}.category.${categorySlug}.title_phrase`)
    });

    description = t(`${base}.category.${categorySlug}.description`, agencyVars);

    h1 = t(`${base}.h1_template`, {
      ...agencyVars,
      suffix: t(`${base}.category.${categorySlug}.h1`)
    });
  } else {
    title = t(`${base}.title_template`, {
      ...agencyVars,
      phrase: t(`${base}.category.${categorySlug}.title_phrase_with_${dealKey}`)
    });

    description = t(
      `${base}.category.${categorySlug}.description_${dealKey}`,
      agencyVars
    );

    h1 = t(`${base}.h1_template`, {
      ...agencyVars,
      suffix: t(`${base}.category.${categorySlug}.h1_with_${dealKey}`)
    });
  }

  return { title, description, h1 };
};

export const getAgencyFilterCategoryStatName = (
  slug: string,
  isCurrentItemPage: boolean
) => {
  const slugPart = slug.includes('/') ? slug.split('/').pop() : slug;
  const prefix = isCurrentItemPage ? 'current-item' : 'agency';
  return `${prefix}-filter-${slugPart}`;
};
